import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { Player, PlayerHistory } from '../types';
import { checkForFinalThrow, checkWinningThrow, applyDiceRules } from '../gameLogic';
import { releaseTournamentMatchByContext } from '../services/tournamentService';
import { handleTurnSounds } from '../Utils/soundHandler';
import {
  logGamePlayedEvent,
  logGameFinishedEvent,
  logDiceCalculatedEvent,
  logTripleFreezeEvent,
  logLostAllPointsEvent,
  logTriplaTriplatEvent,
  logTriplatEvent
} from '../firebaseConfig';
import { useRef } from 'react';

  export const useGameLogic = (
  players: Player[],
  setPlayers: React.Dispatch<React.SetStateAction<Player[]>>,
  currentPlayerIndex: number,
  setCurrentPlayerIndex: React.Dispatch<React.SetStateAction<number>>,
  setGameWon: React.Dispatch<React.SetStateAction<boolean>>,
  setWinnerName: React.Dispatch<React.SetStateAction<string | undefined>>,
  handleSpecialMessage: (message: string) => void,
  saveGameStateToLocalStorage: () => void,
  clearGameState: () => void,
  soundEnabled: boolean,
  gameState?: any,
  setGameState?: any,
    completeMatch?: any,
    setShowMatchStats?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const authContext = useContext(AuthContext);
  const { isAuthenticated, user, updateUserGameData, updateSpecialThrowStats, logPlayerAccuracyData } = authContext;
  const navigate = useNavigate();
  const botGameRef = useRef<{ bot: boolean; botDifficulty: 'easy' | 'medium' | 'hard' } | null>(null);

  // Helper: Bot accuracy map
  const botAccuracyMap = {
    easy: 0.4,
    medium: 0.65,
    hard: 0.85,
  };

  // Helper: Generate bot dice values based on accuracy
  function generateBotDice(difficulty: 'easy' | 'medium' | 'hard') {
    const accuracy = botAccuracyMap[difficulty];
    return Array(3)
      .fill(0)
      .map(() => (Math.random() < accuracy ? Math.floor(Math.random() * 6) + 1 : 0));
  }

  // Helper: Is this a bot game?
  function isBotGame() {
    return !!botGameRef.current?.bot;
  }

  // Helper: Is it the bot's turn?
  function isBotTurn() {
    return isBotGame() && players[currentPlayerIndex]?.id === 'bot';
  }

  // Helper function to validate and log final throw state
  const validateFinalThrowState = (player: Player, action: string) => {
    if (player.isFinalThrow) {
      console.log(`[Final Throw Validation] ${action} - Player: ${player.name}, Score: ${player.score}, isFinalThrow: ${player.isFinalThrow}`);
    }
  };

  // Comprehensive validation function to check all players' final throw states
  const validateAllPlayersFinalThrowState = (action: string) => {
    console.log(`[Final Throw State Check] ${action} - Checking all players:`);
    let inconsistenciesFound = false;
    
    players.forEach((player, index) => {
      const shouldBeInFinalThrow = player.score >= 90 && !player.hasWon;
      if (player.isFinalThrow !== shouldBeInFinalThrow) {
        console.warn(`[Final Throw State Inconsistency] Player ${player.name}: isFinalThrow=${player.isFinalThrow}, shouldBe=${shouldBeInFinalThrow}, score=${player.score}, hasWon=${player.hasWon}`);
        inconsistenciesFound = true;
      } else {
        console.log(`[Final Throw State OK] Player ${player.name}: isFinalThrow=${player.isFinalThrow}, score=${player.score}, hasWon=${player.hasWon}`);
      }
    });
    
    // Auto-fix inconsistencies if found
    if (inconsistenciesFound) {
      fixFinalThrowStateInconsistencies();
    }
  };

  // Function to fix final throw state inconsistencies
  const fixFinalThrowStateInconsistencies = () => {
    console.log('[Final Throw State Fix] Attempting to fix inconsistencies...');
    
    setPlayers(prevPlayers => 
      prevPlayers.map(player => {
        const shouldBeInFinalThrow = player.score >= 90 && !player.hasWon;
        if (player.isFinalThrow !== shouldBeInFinalThrow) {
          console.log(`[Final Throw State Fix] Fixing player ${player.name}: isFinalThrow ${player.isFinalThrow} -> ${shouldBeInFinalThrow}`);
          return {
            ...player,
            isFinalThrow: shouldBeInFinalThrow
          };
        }
        return player;
      })
    );
  };

  const startGame = (
    playerList: { id: string; name: string }[],
    matchCount: number = 1,
    gameOptions?: {
      bot?: boolean;
      botDifficulty?: 'easy' | 'medium' | 'hard';
      tournamentContext?: import('../types').TournamentMatchContext;
    }
  ) => {
    if (gameOptions?.bot) {
      botGameRef.current = { bot: true, botDifficulty: gameOptions.botDifficulty || 'easy' };
    } else {
      botGameRef.current = null;
    }
    const initializedPlayers = playerList.map((player, index) => {
      const isHost = isAuthenticated && user ? player.name === user.name : false;

      return {
        ...player,
        dice: [
          { value: 0, isFrozen: false },
          { value: 0, isFrozen: false },
          { value: 0, isFrozen: false },
        ],
        isCurrent: index === 0,
        score: 0,
        lastAnnouncedScore: 0,
        history: [],
        isFinalThrow: false,
        hasWon: false,
        isHost,
        throws: 0,
      } as Player;
    });
    setPlayers(initializedPlayers);
    setCurrentPlayerIndex(0);
    setGameWon(false);
    if (setShowMatchStats) setShowMatchStats(false);

    // Always (re)initialize game state for a clean start
    if (setGameState) {
      setGameState({
        totalMatches: matchCount,
        currentMatch: 1,
        matchHistory: [],
        isMatchBased: matchCount > 1,
        tournamentContext: gameOptions?.tournamentContext,
      });
    }

    if (!isBotGame()) {
      logGamePlayedEvent('Triple Freezer');
    }
    clearGameState();
    navigate('/game');
  };

  const handleDiceValueChange = (diceIndex: number, value: number) => {
    if (value < 0) value = 0;
    if (value > 6) value = 6;
    setPlayers((prevPlayers) =>
      prevPlayers.map((player, index) =>
        index === currentPlayerIndex
          ? {
              ...player,
              dice: player.dice.map((die, idx) =>
                idx === diceIndex ? { ...die, value } : die
              ),
            }
          : player
      )
    );
    logDiceCalculatedEvent('Triple Freezer', value);
    saveGameStateToLocalStorage();
  };

  const toggleFreeze = (diceIndex: number) => {
    // Prevent freezing during final throw phase
    const current = players[currentPlayerIndex];
    if (current && current.isFinalThrow) {
      return;
    }

    // Prevent freezing dice of a player who is not the current player
    // This also ensures human cannot freeze bot dice and bot cannot freeze human dice
    // because the UI only wires clicks for the current player's card, but we guard here too.
    if (!players[currentPlayerIndex]?.isCurrent) {
      return;
    }

    setPlayers((prevPlayers) =>
      prevPlayers.map((player, index) =>
        index === currentPlayerIndex
          ? {
              ...player,
              dice: player.dice.map((die, idx) =>
                idx === diceIndex ? { ...die, isFrozen: !die.isFrozen } : die
              ),
            }
          : player
      )
    );
    saveGameStateToLocalStorage();
  };

  const handleTurn = (score: number, currentPlayer: any, outcome: string, allDiceFrozenWithSix: boolean) => {
    handleTurnSounds(score, currentPlayer, outcome, allDiceFrozenWithSix, soundEnabled);
  };

  const endTurn = () => {
    const currentPlayer = players[currentPlayerIndex];
    
    // Validate final throw state before processing turn
    validateFinalThrowState(currentPlayer, 'End Turn Start');
    validateAllPlayersFinalThrowState('Before End Turn');
    
    const { score, outcome } = applyDiceRules(currentPlayer.dice);
    const diceValues = currentPlayer.dice.map(die => die.value);
    currentPlayer.throws = (currentPlayer.throws || 0) + 1;
    
    // Only log accuracy data if the current player is the logged-in user
    if (isAuthenticated && user && !isBotGame() && currentPlayer.name === user.name) {
      logPlayerAccuracyData(diceValues);
    }
    
    const allDiceFrozenWithSix = currentPlayer.dice.every(die => die.isFrozen && die.value === 6);
    
    // Debug logging for frozen 666 condition
    if (allDiceFrozenWithSix) {
      console.log(`[Frozen 666 Debug] Player: ${currentPlayer.name}, Outcome: ${outcome}, Score: ${score}`);
    }
    
    handleTurn(score, currentPlayer, outcome, allDiceFrozenWithSix);
    let specialEventMessage = null;
    
    // Check for win conditions first (including frozen 666)
    if (outcome === 'win') {
      specialEventMessage = 'Insta Win!';
      if (!isBotGame()) logTripleFreezeEvent(currentPlayer.name);
      // Only update special throw stats if the current player is the logged-in user
      if (isAuthenticated && user && !isBotGame() && currentPlayer.name === user.name) {
        updateSpecialThrowStats('tripleFreezeThrows');
      }
    } else if (allDiceFrozenWithSix) {
      // This should only trigger if the outcome wasn't 'win' but we have frozen 666
      // This is a fallback for the special "Triple Freeze" message
      specialEventMessage = 'Triple Freeze!';
      if (!isBotGame()) logTripleFreezeEvent(currentPlayer.name);
      // Only update special throw stats if the current player is the logged-in user
      if (isAuthenticated && user && !isBotGame() && currentPlayer.name === user.name) {
        updateSpecialThrowStats('tripleFreezeThrows');
      }
    } else if (outcome === 'lose') {
      specialEventMessage = 'Lost All Points with 1,1,1';
      if (!isBotGame()) logLostAllPointsEvent(currentPlayer.name);
      // Only update special throw stats if the current player is the logged-in user
      if (isAuthenticated && user && !isBotGame() && currentPlayer.name === user.name) {
        updateSpecialThrowStats('lostAllPointsThrows');
      }
    } else if (score === 90) {
      specialEventMessage = 'Tripla-Triplat!';
      if (!isBotGame()) logTriplaTriplatEvent(currentPlayer.name);
      // Only update special throw stats if the current player is the logged-in user
      if (isAuthenticated && user && !isBotGame() && currentPlayer.name === user.name) {
        updateSpecialThrowStats('triplaTriplatThrows');
      }
    } else if (score === 0) {
      specialEventMessage = 'Miss!';
    } else if (outcome === 'freeze') {
      specialEventMessage = 'Freeze!';
    } else if (currentPlayer.dice.every((die) => die.value > 0)) {
      specialEventMessage = 'Triplat!';
      if (!isBotGame()) logTriplatEvent(currentPlayer.name);
      // Only update special throw stats if the current player is the logged-in user
      if (isAuthenticated && user && !isBotGame() && currentPlayer.name === user.name) {
        updateSpecialThrowStats('triplatThrows');
      }
    }
    if (specialEventMessage) {
      // Always proceed with end turn logic even for special events to ensure proper win validation
      handleSpecialMessage(specialEventMessage);
      proceedWithEndTurn(score);
    } else {
      proceedWithEndTurn(score);
    }
    saveGameStateToLocalStorage();
    // Bot auto-turn logic removed from here
  };

  const proceedWithEndTurn = (turnScore: number = 0) => {
    const currentPlayer = players[currentPlayerIndex];
    
    // Validate final throw state before processing
    validateFinalThrowState(currentPlayer, 'Proceed With End Turn Start');
    
    const { score, outcome } = applyDiceRules(currentPlayer.dice);
    let hasGameWon = false;
  
    const updatedPlayers = players.map((player, index) => {
      if (index === currentPlayerIndex) {
        let updatedScore = player.score + score;
  
        // Cap the updated score at 90
        if (updatedScore > 90) {
          updatedScore = 90;
        }
  
        let isFinalThrow = player.isFinalThrow;
        let hasWon = player.hasWon;
  
        let newHistoryEntry: PlayerHistory = {
          diceValues: player.dice.map((die) => die.value),
          isFrozen: player.dice.map((die) => die.isFrozen),
          turnScore: Math.min(score, 90 - player.score),
          isFinalThrowAttempt: isFinalThrow
        };
  
        if (!player.isFinalThrow) {
          newHistoryEntry.turnScore = Math.min(score, 90 - player.score);
  
          if (outcome === 'win') {
            hasWon = true;
            hasGameWon = true;
            newHistoryEntry.hasWonAttempt = true;
          } else if (outcome === 'lose') {
            updatedScore = 0;
            newHistoryEntry.turnScore = 0;
          } else if (outcome === 'continue' && checkForFinalThrow(updatedScore)) {
            isFinalThrow = true;
            console.log(`[Final Throw State Change] Player ${player.name} entered final throw state with score ${updatedScore}`);
          }
        } else {
          // FINAL THROW LOGIC - must validate using the effective turn score from rules (e.g., Triplat doubles)
          newHistoryEntry.isFinalThrowAttempt = true;

          const actualDiceSum = currentPlayer.dice.reduce((sum, die) => sum + die.value, 0);

          // Determine effective score of this final throw. If rules yielded a computed 'score' (e.g., Triplat -> doubled),
          // use that. For special 'win' outcomes (e.g., 6-6-6), score already carries the correct 90.
          const effectiveTurnScore = outcome === 'win'
            ? score
            : (outcome === 'continue' ? score : actualDiceSum);

          console.log(`[Final Throw Validation] Player ${player.name} final throw - Actual dice sum: ${actualDiceSum}, Effective score: ${effectiveTurnScore}, Outcome: ${outcome}`);

          if (checkWinningThrow(effectiveTurnScore) || outcome === 'win') {
            hasWon = true;
            hasGameWon = true;
            newHistoryEntry.hasWonAttempt = true;
            newHistoryEntry.turnScore = effectiveTurnScore;
            console.log(`[Final Throw Win] Player ${player.name} won with final throw - Score: ${newHistoryEntry.turnScore}, Outcome: ${outcome}`);
          } else {
            newHistoryEntry.hasWonAttempt = false;
            newHistoryEntry.turnScore = effectiveTurnScore;
            console.log(`[Final Throw Continue] Player ${player.name} continues with final throw score: ${effectiveTurnScore}`);
          }

          // Keep displayed score fixed at 90 during final throw sequence
          updatedScore = 90;
        }
  
        const updatedPlayer = {
          ...player,
          score: updatedScore,
          history: [...player.history, newHistoryEntry],
          dice: player.dice.map((die) => ({ ...die, value: 0, isFrozen: false })),
          isFinalThrow,
          hasWon,
        };
        
        // Validate final throw state after update
        validateFinalThrowState(updatedPlayer, 'Proceed With End Turn End');
        
        return updatedPlayer;
      }
      return player;
    });
  
    setPlayers(updatedPlayers);
  
        if (hasGameWon) {
      setWinnerName(currentPlayer.name);
      if (!isBotGame()) logGameFinishedEvent('Triple Freezer', currentPlayer.name, currentPlayer.score);
      
      // Handle match-based game
      if (gameState?.isMatchBased && completeMatch) {
        const winner = updatedPlayers.find((p) => p.hasWon);
          const matchStats = {
          matchNumber: gameState.currentMatch,
          winner: winner?.name || '',
          finalScore: winner?.score || 0,
          turnsPlayed: winner?.history.length || 0,
            playerStats: updatedPlayers.map(player => {
            const triples = player.history.filter(entry => {
              const allPositive = entry.diceValues.every((v: number) => v > 0);
              const noFrozen = Array.isArray(entry.isFrozen) ? entry.isFrozen.every((f: boolean) => !f) : true;
              const uniqueVals: number[] = Array.from(new Set<number>(entry.diceValues as number[]));
              const isTripleTriple = uniqueVals.length === 1 && uniqueVals[0] >= 2 && uniqueVals[0] <= 6;
              // Triplat only when no dice are frozen, exclude triple-of-a-kind
              return allPositive && noFrozen && !isTripleTriple;
            }).length;
            const tripleTriplesCount = player.history.filter(entry => {
                const uniqueVals: number[] = Array.from(new Set<number>(entry.diceValues as number[]));
                return uniqueVals.length === 1 && uniqueVals[0] >= 2 && uniqueVals[0] <= 6; // include 6,6,6
              }).length;
            const finalThrows = player.history.filter(entry => entry.isFinalThrowAttempt).length;
            
            // Debug logging for final throws and triples
            if (finalThrows > 0) {
              console.log(`[Match Stats Debug] Player ${player.name}:`);
              console.log(`  - Total history entries: ${player.history.length}`);
              console.log(`  - Final throw entries: ${finalThrows}`);
              console.log(`  - Triples count: ${triples}`);
              player.history.forEach((entry, index) => {
                if (entry.isFinalThrowAttempt) {
                  console.log(`  - Final throw entry ${index}: dice=${entry.diceValues}, isTriple=${entry.diceValues.every(v => v > 0)}`);
                }
              });
            }
            
            return {
              playerId: player.id,
              name: player.name,
              score: player.score,
              throws: player.throws,
              // Per-turn accuracy: hit if any die > 0 on that turn
              accuracy: player.history.length > 0 ?
                ((player.history.filter(entry => entry.diceValues.some(v => v > 0)).length / player.history.length) * 100) : 0,
              // New rule: triple-triples also count toward total triples
              triples: triples + tripleTriplesCount,
              tripleTriples: tripleTriplesCount,
              finalThrows: finalThrows,
              maxTurnScore: player.history.reduce((max, entry) => Math.max(max, entry.turnScore), 0),
              frozenDiceCount: player.history.reduce((count, entry) => 
                count + entry.diceValues.filter((_, i) => entry.isFrozen[i]).length, 0),
              history: player.history,
            };
          })
        };
        
        completeMatch(matchStats);

        // Also persist the logged-in user's all-time profile stats for THIS
        // game — so tournament and best-of-series games log the same as casual
        // single games (games played/won, accuracy, fastest win, achievement
        // counters, etc.). Purely additive data capture: it does not affect
        // turn resolution, scoring, or match completion. The per-event special
        // throw counters are recorded separately during play and are NOT touched
        // by updateUserGameData, so nothing is double-counted.
        if (isAuthenticated && user && !isBotGame()) {
          const matchWinner = updatedPlayers.find((p) => p.hasWon);
          const userPlayer = updatedPlayers.find((p) => p.name === user.name);
          if (userPlayer) {
            updateUserGameData({
              date: new Date().toISOString(),
              winner: matchWinner?.name || '',
              players: [
                {
                  name: userPlayer.name,
                  score: userPlayer.score,
                  isFinalThrow: userPlayer.isFinalThrow,
                  hasWon: userPlayer.hasWon,
                  throws: userPlayer.throws,
                  dice: userPlayer.dice,
                  history: userPlayer.history,
                },
              ],
            });
          }
        }
      } else {
        // Single game mode
        setGameWon(true);
        if (isAuthenticated && user && !isBotGame()) {
          const winner = updatedPlayers.find((p) => p.hasWon);
          const userPlayer = updatedPlayers.find((p) => p.name === user.name);
          if (userPlayer) {
            const gameData = {
              date: new Date().toISOString(),
              winner: winner?.name || '',
              players: [
                {
                  name: userPlayer.name,
                  score: userPlayer.score,
                  isFinalThrow: userPlayer.isFinalThrow,
                  hasWon: userPlayer.hasWon,
                  throws: userPlayer.throws,
                  dice: userPlayer.dice,
                  history: userPlayer.history,
                },
              ],
            };
            updateUserGameData(gameData);
          }
        }
      }
    } else {
      nextTurn();
    }
    validateAllPlayersFinalThrowState('After Turn Complete');
    saveGameStateToLocalStorage();
  };

  const nextTurn = () => {
    setCurrentPlayerIndex((prevIndex) => (prevIndex + 1) % players.length);
    setPlayers((prevPlayers) =>
      prevPlayers.map((player, index) => ({
        ...player,
        isCurrent: index === (currentPlayerIndex + 1) % prevPlayers.length,
      }))
    );
    validateAllPlayersFinalThrowState('After Next Turn');
    saveGameStateToLocalStorage();
  };

  const [showQuitModal, setShowQuitModal] = useState(false);

  const handleQuitGame = () => {
    setShowQuitModal(true);
  };

  const confirmQuit = () => {
    // Hand a tournament match back before wiping the local state. Quitting used
    // to leave `status:'active'` on the server with the local game state gone —
    // the match was then unplayable by either player and an admin had to reset
    // it or award a walkover. Fire-and-forget: quitting must never be blocked
    // by the network, and the reconciler/take-over path covers a failed write.
    const quitCtx = gameState?.tournamentContext;
    if (quitCtx) {
      releaseTournamentMatchByContext(quitCtx).catch(() => {});
    }

    // Fully reset persisted state
    clearGameState();

    // Reset in-memory state
    setPlayers([]);
    setCurrentPlayerIndex(0);
    setGameWon(false);
    setWinnerName(undefined);
    if (setShowMatchStats) setShowMatchStats(false);
    if (setGameState) {
      setGameState({
        totalMatches: 1,
        currentMatch: 1,
        matchHistory: [],
        isMatchBased: false,
      });
    }

    setShowQuitModal(false);
    navigate('/');
  };

  const cancelQuit = () => {
    setShowQuitModal(false);
  };

  const handleRestartGame = () => {
    // Reset per-game stats but keep player identities for an immediate rematch
    const newPlayers = players.map(player => ({
      ...player,
      score: 0,
      dice: player.dice.map(die => ({ value: 0, isFrozen: false })),
      history: [],
      hasWon: false,
      isFinalThrow: false,
      throws: 0,
      matchWins: 0,
      totalMatches: 0,
    }));

    setPlayers(newPlayers);
    setCurrentPlayerIndex(0);
    setGameWon(false);
    if (setShowMatchStats) setShowMatchStats(false);

    // Reset series state to a single non-match-based game for rematch
    if (setGameState) {
      setGameState({
        totalMatches: 1,
        currentMatch: 1,
        matchHistory: [],
        isMatchBased: false,
      });
    }

    clearGameState();
    saveGameStateToLocalStorage();
    navigate('/game');
  };

  return {
    startGame,
    handleDiceValueChange,
    toggleFreeze,
    endTurn,
    proceedWithEndTurn,
    nextTurn,
    handleQuitGame,
    handleRestartGame,
    isBotGame,
    isBotTurn,
    generateBotDice,
    botGameRef, // export for App.tsx
    showQuitModal,
    confirmQuit,
    cancelQuit,
  };
};